import axios from 'axios'
import axiosRetry from 'axios-retry'
import { captureException } from './errors'
import utils from '../../../shared/utils'

const { sleep } = utils

const designHuddleDomain = import.meta.env.VITE_DESIGN_HUDDLE_DOMAIN

export const loadEditorSDK = () => {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = 'https://cdn.designhuddle.com/jssdk/v1/lib.min.js'

  const existing = document.getElementsByTagName('script')[0]
  existing.parentNode.insertBefore(script, existing)
}

export const templateCategories = {
  newsletters: 1,
  flyers: 2,
  digitalSignages: 3,
  events: 4,
}

export const designHuddleDefaultEventTemplate = {
  template_id: 54,
  template_title: 'Event Image',
  primary_template_category: {
    template_category_id: 1,
    category_name: 'Design Type',
  },
  primary_template_category_item: {
    template_category_item_id: 4,
    item_name: 'Events',
  },
  page_count: 1,
  dimensions: {
    width: 800,
    height: 700,
    unit_type: {
      unit_type_id: 'px',
      unit_type_name: 'Pixels',
    },
  },
}

export const printExportOptions = {
  text_to_outlines: true,
}

const TRANSIENT_STATUS_CODES = new Set([502, 503, 504])

export const toProjectTitle = (title) => (title || '').slice(0, 50).trim()

export class DesignHuddleClient {
  constructor(access_token) {
    this.designHuddleClient = axios.create({
      baseURL: `https://${designHuddleDomain}`,
      headers: { authorization: `Bearer ${access_token}` },
    })

    axiosRetry(this.designHuddleClient, {
      retries: 5,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) => !error.response || TRANSIENT_STATUS_CODES.has(error.response.status),
    })
  }

  startProjectExport = async (project_id, filename, format, options = {}) => {
    const { data: result } = await this.designHuddleClient.post(`/api/projects/${project_id}/export`, { ...options, format, filename })
    if (result && result.success && result.data) {
      return result.data.job_id
    } else {
      throw new Error('Error starting the design huddle project export.')
    }
  }

  pollJobExport = async (project_id, job_id) => {
    const { data: result } = await this.designHuddleClient.get(`/api/projects/${project_id}/export/jobs/${job_id}`, {})
    if (result.data.completed) {
      return result.data
    } else {
      await sleep(1000)
      return this.pollJobExport(project_id, job_id)
    }
  }

  exportProject = async (projectId, title, format = 'pdf', options = {}) => {
    const jobId = await this.startProjectExport(projectId, title, format, options)
    return this.pollJobExport(projectId, jobId)
  }

  cloneProject = async (project_id, title) => {
    try {
      const { data: result } = await this.designHuddleClient.post(`/api/projects/${project_id}/clone`, { project_title: toProjectTitle(title) })
      if (result) {
        return result.data.project_id
      }
    } catch (error) {
      captureException(error)
    }
  }
}

export default {
  loadEditorSDK,
  DesignHuddleClient,
}
